
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ARCHIVE_ID } from "@/utils/contentFormUtils";

interface ContentData {
  id: string;
  title: string;
  url: string;
  description?: string;
  content_type: string;
  collection_id?: string;
  archived: boolean;
  [key: string]: any;
}

export const useEditContentData = (contentId: string, isOpen: boolean) => {
  const [contentData, setContentData] = useState<ContentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchContentData = async () => {
      if (!isOpen || !contentId || !user) {
        console.log('[useEditContentData] Skipping fetch - conditions not met:', { 
          isOpen, 
          contentId: !!contentId, 
          user: !!user 
        });
        return;
      }
      
      console.log('[useEditContentData] Fetching data for:', contentId);
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch the content item
        const { data: contentItem, error: contentError } = await supabase
          .from('content_items')
          .select('*')
          .eq('id', contentId)
          .single();
        
        if (contentError) {
          throw contentError;
        }

        if (!contentItem) {
          throw new Error(`Content item with ID ${contentId} not found`);
        }

        // Fetch the collection association
        const { data: collectionData, error: collectionError } = await supabase
          .from('content_collections')
          .select('collection_id')
          .eq('content_id', contentId)
          .maybeSingle();
        
        if (collectionError) {
          console.error("[useEditContentData] Error fetching content collection:", collectionError);
        }
        
        // If item is archived, set collection_id to ARCHIVE_ID regardless of any other collection
        const combinedData = {
          ...contentItem,
          collection_id: contentItem.archived ? ARCHIVE_ID : (collectionData?.collection_id || null)
        };
        
        console.log('[useEditContentData] Data fetched successfully:', {
          contentItem,
          collectionData,
          combinedData,
          isArchived: contentItem.archived
        });
        
        setContentData(combinedData);
      } catch (err: any) {
        console.error('[useEditContentData] Error fetching content item:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContentData();
  }, [contentId, isOpen, user]);

  return { contentData, isLoading, error };
};
